import { BofuDocumentRelationship, PersonFormsConfig } from "@interfaces/response";
import { PERSON_CATALOG } from "@helpers/constantes";

export type PersonFormFieldConfig = {
  enabled: boolean;
  required?: boolean;
};

/** Campos que pueden estar en el formulario de persona. */
export const PERSON_FORM_FIELD_CODES = [
  "cell_phone_number",
  "occupation",
  "email",
  "rfc",
  "curp",
  "civil_status_id",
  "matrimonial_regimes_id",
  "spouse_id",
  "birth_date",
  "birth_state",
  "sex",
  "domicilio_operativo",
  "domicilio_particular",
] as const;

/** Campos que un cónyuge (PTCAV) no debe editar: la relación la define el principal vía `spouse_id`. */
export const PTCAV_EXCLUDED_FIELD_CODES: readonly PersonFormFieldCode[] = [
  "civil_status_id",
  "matrimonial_regimes_id",
  "spouse_id",
];

export type PersonFormFieldCode = (typeof PERSON_FORM_FIELD_CODES)[number];

const DISABLED: PersonFormFieldConfig = { enabled: false };
const REQUIRED: PersonFormFieldConfig = { enabled: true, required: true };
const OPTIONAL: PersonFormFieldConfig = { enabled: true, required: false };

/** Base: todos los campos del catálogo deshabilitados. */
export const ALL_FIELDS_DISABLED: Record<PersonFormFieldCode, PersonFormFieldConfig> = {
  cell_phone_number: DISABLED,
  occupation: DISABLED,
  email: DISABLED,
  rfc: DISABLED,
  curp: DISABLED,
  civil_status_id: DISABLED,
  matrimonial_regimes_id: DISABLED,
  spouse_id: DISABLED,
  birth_date: DISABLED,
  birth_state: DISABLED,
  sex: DISABLED,
  domicilio_operativo: DISABLED,
  domicilio_particular: DISABLED,
};

/**
 * Tipos de persona permitidos en el hub TBPER (`docs_persona`).
 *
 * El orden del arreglo define la jerarquía de visualización:
 * 1. representante_legal
 * 2. co_acreditado
 * 3. garante
 * 4. aval (`accionista_mayoritario`, el mismo criterio que `identifyPersons`)
 * 5. propietario_real
 * 6. segundo_aval
 * 7. obligado_solidario
 * 8. conyuge_aval
 *
 * `filterPersonsForFormHub` filtra solo estos códigos y ordena la lista
 * según este índice (menor = primero). Si una persona tiene varios tipos,
 * se usa el de mayor jerarquía (el índice más bajo).
 */
export const PERSON_FORM_HUB_TYPE_CODES = [
  PERSON_CATALOG.representante_legal.code,
  PERSON_CATALOG.co_acreditado.code,
  PERSON_CATALOG.garante.code,
  PERSON_CATALOG.accionista_mayoritario.code,
  PERSON_CATALOG.propietario_real.code,
  PERSON_CATALOG.segundo_aval.code,
  PERSON_CATALOG.obligado_solidario.code,
  PERSON_CATALOG.conyuge_aval.code,
] as const;

/** Perfil compartido PTRL / PTCOA / PFGAR. */
const REPRESENTANTE_LIKE_FIELDS: Record<PersonFormFieldCode, PersonFormFieldConfig> = {
  ...ALL_FIELDS_DISABLED,
  cell_phone_number: REQUIRED,
  email: REQUIRED,
  rfc: REQUIRED,
};

/**
 * Config local de FORMULARIOS_PERSONA por tipo de persona (PTAM y PFOBS comparten campos).
 * Se usa completa cuando `tabs_bofu_config` no trae config y como base del merge cuando la trae parcial.
 */
export const DEFAULT_PERSON_FORMS_CONFIG: PersonFormsConfig = {
  [PERSON_CATALOG.representante_legal.code]: {
    code: PERSON_CATALOG.representante_legal.code,
    label: "Representante legal",
    enabled: true,
    fields: REPRESENTANTE_LIKE_FIELDS,
  },
  [PERSON_CATALOG.co_acreditado.code]: {
    code: PERSON_CATALOG.co_acreditado.code,
    label: "Co acreditado",
    enabled: true,
    fields: REPRESENTANTE_LIKE_FIELDS,
  },
  [PERSON_CATALOG.garante.code]: {
    code: PERSON_CATALOG.garante.code,
    label: "Garante",
    enabled: true,
    fields: REPRESENTANTE_LIKE_FIELDS,
  },
  [PERSON_CATALOG.accionista_mayoritario.code]: {
    code: PERSON_CATALOG.accionista_mayoritario.code,
    label: "Accionista mayoritario",
    enabled: true,
    fields: {
      ...ALL_FIELDS_DISABLED,
      cell_phone_number: REQUIRED,
      occupation: REQUIRED,
      civil_status_id: REQUIRED,
      matrimonial_regimes_id: OPTIONAL,
      spouse_id: OPTIONAL,
    },
  },
  [PERSON_CATALOG.propietario_real.code]: {
    code: PERSON_CATALOG.propietario_real.code,
    label: "Propietario real",
    enabled: true,
    fields: {
      ...ALL_FIELDS_DISABLED,
      cell_phone_number: REQUIRED,
      email: REQUIRED,
      rfc: REQUIRED,
      curp: REQUIRED,
      civil_status_id: REQUIRED,
      matrimonial_regimes_id: OPTIONAL,
      birth_date: REQUIRED,
      birth_state: REQUIRED,
      sex: REQUIRED,
      domicilio_particular: REQUIRED,
    },
  },
  [PERSON_CATALOG.segundo_aval.code]: {
    code: PERSON_CATALOG.segundo_aval.code,
    label: "Segundo aval",
    enabled: true,
    fields: {
      ...ALL_FIELDS_DISABLED,
      occupation: REQUIRED,
      civil_status_id: REQUIRED,
      matrimonial_regimes_id: OPTIONAL,
      spouse_id: OPTIONAL,
    },
  },
  [PERSON_CATALOG.obligado_solidario.code]: {
    code: PERSON_CATALOG.obligado_solidario.code,
    label: "Obligado solidario",
    enabled: true,
    fields: {
      ...ALL_FIELDS_DISABLED,
      cell_phone_number: REQUIRED,
      occupation: REQUIRED,
      civil_status_id: REQUIRED,
      matrimonial_regimes_id: OPTIONAL,
      spouse_id: OPTIONAL,
    },
  },
  [PERSON_CATALOG.conyuge_aval.code]: {
    code: PERSON_CATALOG.conyuge_aval.code,
    label: "Cónyuge aval",
    enabled: true,
    fields: {
      ...ALL_FIELDS_DISABLED,
      cell_phone_number: REQUIRED,
      email: REQUIRED,
      occupation: REQUIRED,
      curp: REQUIRED,
      domicilio_particular: REQUIRED,
    },
  },
};

/**
 * Relaciones de documento (TBPER) que pide cada tipo de persona.
 * Un tipo que no está aquí no pide documentos en el hub.
 */
export const DOCUMENT_RELATIONSHIPS_BY_PERSON_TYPE: Record<string, BofuDocumentRelationship[]> = {
  [PERSON_CATALOG.representante_legal.code]: ["representante_legal"],
  [PERSON_CATALOG.co_acreditado.code]: ["co_acreditado"],
  [PERSON_CATALOG.garante.code]: ["garante"],
  [PERSON_CATALOG.accionista_mayoritario.code]: ["aval"],
  [PERSON_CATALOG.propietario_real.code]: ["propietario_real"],
  [PERSON_CATALOG.segundo_aval.code]: ["segundo_aval"],
  [PERSON_CATALOG.obligado_solidario.code]: ["obligado_solidario"],
  [PERSON_CATALOG.conyuge_aval.code]: ["conyuge_aval"],
};
